// src/components/BookPurchase.jsx
//
// Handles the buy flow for a single book. A book can be sold as a PDF,
// a hard copy, or both — this component treats the two as genuinely
// separate products:
//  - only formats that are actually available (and, for hard copy, in
//    stock) can be selected
//  - if only one format is on offer, it's chosen automatically and the
//    format picker doesn't render at all
//  - hard copy always collects delivery details (phone + address);
//    PDF never does
//  - whichever format the buyer picks is the only thing sent to
//    checkout — the backend (api/create-book-checkout.js) re-validates
//    this independently, so this component is UX, not the source of truth
import React, { useMemo, useState } from "react";

const API_BASE = "https://jabuzwane.vercel.app/api";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatCurrency(val, currency = "ZAR") {
  const amount = Number(val);
  if (!Number.isFinite(amount)) return "";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

const BookPurchase = ({ book }) => {
  const currency = book.currency || "ZAR";

  // A format only counts as purchasable if it's switched on AND actually
  // has what it needs to fulfil an order: a PDF needs an uploaded file,
  // a hard copy needs stock (null/undefined stock = unlimited).
  const pdfInStock = book.digitalAvailable !== false && Boolean(book.pdfUrl && book.pdfPublicId);
  const hardCopyInStock =
    book.physicalAvailable !== false &&
    !(Number.isInteger(book.physicalStock) && book.physicalStock <= 0);

  const pdfPrice = book.price;
  const hardCopyPrice = book.physicalPrice ?? book.price;

  const [step, setStep] = useState("idle"); // idle | form
  const [format, setFormat] = useState(() => {
    if (pdfInStock && hardCopyInStock) return "";
    if (pdfInStock) return "pdf";
    if (hardCopyInStock) return "hardCopy";
    return "";
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bothAvailable = pdfInStock && hardCopyInStock;

  const validate = () => {
    if (!format) return "Please choose a format.";
    if (!email.trim() || !EMAIL_RE.test(email.trim())) return "Please enter a valid email address.";
    if (format === "hardCopy") {
      if (!phone.trim()) return "Cellphone number is required for a hard copy.";
      if (!address.trim()) return "Delivery address is required for a hard copy.";
    }
    return null;
  };

  const startCheckout = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/create-book-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookId: book.id,
          format,
          buyerEmail: email.trim(),
          buyerName: name.trim(),
          ...(format === "hardCopy"
            ? { buyerPhone: phone.trim(), buyerAddress: address.trim() }
            : {}),
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // non-JSON error response — fall through to the generic message below
      }

      if (!res.ok || !data.redirectUrl) {
        setError(data.message || "Could not start checkout. Please try again.");
        setLoading(false);
        return;
      }

      try {
        sessionStorage.setItem("jz_last_order_id", data.orderId);
      } catch {
        // sessionStorage can fail in private/incognito contexts — non-fatal
      }
      window.location.href = data.redirectUrl;
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  };

  if (book.available === false || (!pdfInStock && !hardCopyInStock)) {
    const label =
      book.available === false
        ? "Coming soon"
        : (book.physicalAvailable !== false || book.digitalAvailable !== false)
        ? "Sold out"
        : "Coming soon";
    return <span className="bk-btn bk-btn--disabled">{label}</span>;
  }

  if (step !== "form") {
    return (
      <button type="button" className="bk-btn bk-btn--primary" onClick={() => setStep("form")}>
        Buy now
      </button>
    );
  }

  return (
    <form className="bk-purchase-form" onSubmit={startCheckout} noValidate>
      {bothAvailable && (
        <fieldset className="bk-purchase-form__formats">
          <legend className="bk-purchase-form__formats-legend">Choose a format</legend>
          <label>
            <input
              type="radio"
              name={`format-${book.id}`}
              value="pdf"
              checked={format === "pdf"}
              onChange={() => setFormat("pdf")}
            />
            PDF (instant download){pdfPrice != null ? ` — ${formatCurrency(pdfPrice, currency)}` : ""}
          </label>
          <label>
            <input
              type="radio"
              name={`format-${book.id}`}
              value="hardCopy"
              checked={format === "hardCopy"}
              onChange={() => setFormat("hardCopy")}
            />
            Hard copy (delivered){hardCopyPrice != null ? ` — ${formatCurrency(hardCopyPrice, currency)}` : ""}
          </label>
        </fieldset>
      )}

      <input
        type="text"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bk-purchase-form__input"
        disabled={loading}
        autoComplete="name"
      />
      <input
        type="email"
        required
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="bk-purchase-form__input"
        disabled={loading}
        autoComplete="email"
      />

      {format === "hardCopy" && (
        <>
          <input
            type="tel"
            required
            placeholder="Cellphone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="bk-purchase-form__input"
            disabled={loading}
            autoComplete="tel"
          />
          <textarea
            required
            placeholder="Delivery address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="bk-purchase-form__input"
            rows={2}
            disabled={loading}
            autoComplete="street-address"
          />
        </>
      )}

      {error && (
        <div className="bk-purchase-form__error" role="alert">
          {error}
        </div>
      )}

      <div className="bk-purchase-form__actions">
        <button
          type="button"
          className="bk-btn bk-btn--ghost"
          onClick={() => {
            setStep("idle");
            setError(null);
          }}
          disabled={loading}
        >
          Cancel
        </button>
        <button type="submit" className="bk-btn bk-btn--primary" disabled={loading || !format}>
          {loading ? "Redirecting…" : "Continue to payment"}
        </button>
      </div>
    </form>
  );
};

export default BookPurchase;